/**
 * Minimal getUserMedia photo capture for desktop browsers.
 *
 * Rendered with plain DOM rather than React so it can be called from anywhere
 * (including the react-native-image-picker shim, which has a callback API and
 * no component tree of its own).
 *
 * Resolves with `null` if the operator cancels or no camera is available.
 */

export type CapturedPhoto = {
  blob: Blob;
  uri: string;
  width: number;
  height: number;
  type: string;
  name: string;
};

function styleEl(el: HTMLElement, styles: Partial<CSSStyleDeclaration>): void {
  Object.assign(el.style, styles);
}

export function isCameraAvailable(): boolean {
  return (
    typeof navigator !== 'undefined' &&
    Boolean(navigator.mediaDevices) &&
    typeof navigator.mediaDevices.getUserMedia === 'function'
  );
}

export async function capturePhoto(): Promise<CapturedPhoto | null> {
  if (!isCameraAvailable()) {
    throw new Error('This browser does not expose a camera (MediaDevices API unavailable).');
  }

  const stream = await navigator.mediaDevices.getUserMedia({
    video: {facingMode: 'environment'},
    audio: false,
  });

  return new Promise<CapturedPhoto | null>((resolve, reject) => {
    const overlay = document.createElement('div');
    styleEl(overlay, {
      position: 'fixed',
      inset: '0',
      zIndex: '2147483647',
      background: 'rgba(0,0,0,0.88)',
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      gap: '16px',
    });

    const video = document.createElement('video');
    video.autoplay = true;
    video.playsInline = true;
    video.muted = true;
    video.srcObject = stream;
    styleEl(video, {
      maxWidth: '90vw',
      maxHeight: '75vh',
      borderRadius: '8px',
      background: '#000',
    });

    const buttonRow = document.createElement('div');
    styleEl(buttonRow, {display: 'flex', gap: '12px'});

    const makeButton = (label: string, primary: boolean) => {
      const button = document.createElement('button');
      button.type = 'button';
      button.textContent = label;
      styleEl(button, {
        padding: '10px 24px',
        fontSize: '15px',
        borderRadius: '6px',
        border: primary ? 'none' : '1px solid #ffffff',
        background: primary ? '#ffffff' : 'transparent',
        color: primary ? '#111111' : '#ffffff',
        cursor: 'pointer',
      });
      return button;
    };

    const captureButton = makeButton('Capture', true);
    const cancelButton = makeButton('Cancel', false);

    const cleanup = () => {
      stream.getTracks().forEach(track => track.stop());
      if (overlay.parentNode) {
        overlay.parentNode.removeChild(overlay);
      }
      document.removeEventListener('keydown', onKeyDown);
    };

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        cleanup();
        resolve(null);
      }
    };

    cancelButton.addEventListener('click', () => {
      cleanup();
      resolve(null);
    });

    captureButton.addEventListener('click', () => {
      const width = video.videoWidth;
      const height = video.videoHeight;
      const canvas = document.createElement('canvas');
      canvas.width = width;
      canvas.height = height;
      const context = canvas.getContext('2d');
      if (!context) {
        cleanup();
        reject(new Error('Unable to grab a frame from the camera.'));
        return;
      }
      context.drawImage(video, 0, 0, width, height);
      canvas.toBlob(blob => {
        cleanup();
        if (!blob) {
          reject(new Error('Unable to encode the captured photo.'));
          return;
        }
        resolve({
          blob,
          uri: URL.createObjectURL(blob),
          width,
          height,
          type: 'image/jpeg',
          name: `capture-${Date.now()}.jpg`,
        });
      }, 'image/jpeg', 0.92);
    });

    document.addEventListener('keydown', onKeyDown);
    buttonRow.appendChild(captureButton);
    buttonRow.appendChild(cancelButton);
    overlay.appendChild(video);
    overlay.appendChild(buttonRow);
    document.body.appendChild(overlay);
  });
}
